import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import { eventAPI } from '../../actions/imsServer';
import { EVENT_TYPES, API_PAGE_SIZE } from '../../constants';
import { formatDateTimeDisplay } from '../../utils/base';
import { getTableStyles } from '../../components/table/styles';

export const EventAdminDetail = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const eventData = location.state?.eventData;
    const [eventDetail, setEventDetail] = useState(null);
    const [participations, setParticipations] = useState([]);
    const [organizationRankings, setOrganizationRankings] = useState([]);
    const [results, setResults] = useState([]);
    const [organizationResults, setOrganizationResults] = useState([]);
    const [organizationStatistics, setOrganizationStatistics] = useState([]); // 기관별 통계 정보
    const [resultsLoading, setResultsLoading] = useState(false);
    const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false);
    const [hasMoreResults, setHasMoreResults] = useState(true);
    const [, setResultsOffset] = useState(0);
    const resultsOffsetRef = useRef(0);
    const [, setResultsTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [, setOffset] = useState(0);
    const offsetRef = useRef(0);
    const [, setTotalCount] = useState(0);
    const [imageUrl, setImageUrl] = useState(null);
    const tableRef = useRef(null);
    const resultsTableRef = useRef(null);

    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    // 이벤트 데이터가 없으면 목록으로 리다이렉트
    useEffect(() => {
        if (!eventData) {
            navigate('/events/admin', { replace: true });
            return;
        }
    }, [eventData, navigate]);

    // 이벤트 상세 정보 가져오기
    useEffect(() => {
        const fetchEventDetail = async () => {
            if (!eventData?.id) return;

            try {
                setLoading(true);

                // 이벤트 상세 정보 가져오기
                const eventResponse = await eventAPI.getEvents({
                    id: eventData.id,
                    expand: 'user',
                });
                const events = eventResponse?.data?.events || eventResponse?.events || [];
                const event = events[0] || eventResponse?.data?.event || eventResponse?.event;

                if (event) {
                    setEventDetail(event);
                    setImageUrl(event.file || event.imageUrl || null);
                }
            } catch (err) {
                console.error('Failed to fetch event detail:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchEventDetail();
    }, [eventData]);

    // 이벤트 참여 데이터 가져오기 (무한 스크롤)
    const fetchParticipations = useCallback(async (reset = false) => {
        if (!eventData?.id) return;
        if (isLoadingMore && !reset) return;

        const currentOffset = reset ? 0 : offsetRef.current;

        if (reset) {
            setLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const participationResponse = await eventAPI.getEventParticipations({
                eventId: eventData.id,
                expand: 'user',
                offset: currentOffset,
                limit: ITEMS_PER_PAGE,
                state: '0,2',
            });

            const participationsData = Array.isArray(participationResponse?.data?.eventParticipations)
                ? participationResponse.data.eventParticipations
                : Array.isArray(participationResponse?.eventParticipations)
                    ? participationResponse.eventParticipations
                    : Array.isArray(participationResponse?.data)
                        ? participationResponse.data
                        : [];

            const total = participationResponse?.data?.total || participationResponse?.total || 0;
            setTotalCount(total);

            // 랭킹제(type=1)인 경우 기관별로 그룹화
            if (eventDetail?.type === 1) {
                // 기관별로 그룹화하여 총 응모 포인트 계산
                const orgMap = new Map();
                participationsData.forEach(participation => {
                    const orgId = participation.organizationId;
                    const orgName = participation.organizationId === 0
                        ? (participation.user?.mainOrganizationName || t('no_organization'))
                        : (participation.organization?.name || participation.organizationName || t('no_organization'));
                    const userName = participation.user?.name || t('no_user');
                    const usedPoint = participation.usedPoint || 0;

                    if (!orgMap.has(orgId)) {
                        orgMap.set(orgId, {
                            organizationId: orgId,
                            organizationName: orgName,
                            totalPoint: 0,
                            topUser: userName,
                            topUserPoint: usedPoint,
                        });
                    }

                    const orgData = orgMap.get(orgId);
                    orgData.totalPoint += usedPoint;

                    // 기관 내 1위 사용자 업데이트
                    if (usedPoint > orgData.topUserPoint) {
                        orgData.topUser = userName;
                        orgData.topUserPoint = usedPoint;
                    }
                });

                // 기관별 데이터를 배열로 변환 (API에서 이미 정렬되어 오므로 순서대로 숫자만 매김)
                const orgRankings = Array.from(orgMap.values())
                    .map((org, index) => ({
                        rank: (reset ? 0 : organizationRankings.length) + index + 1,
                        organizationId: org.organizationId,
                        organizationName: org.organizationName,
                        topUser: org.topUser,
                        totalPoint: org.totalPoint,
                    }));

                if (reset) {
                    setOrganizationRankings(orgRankings);
                } else {
                    setOrganizationRankings(prev => {
                        const prevArray = Array.isArray(prev) ? prev : [];
                        const combined = [...prevArray, ...orgRankings];
                        const unique = combined.filter(
                            (org, index, self) =>
                                index === self.findIndex(o => o.organizationId === org.organizationId)
                        );
                        // 순서대로 rank 재설정
                        return unique.map((org, index) => ({ ...org, rank: index + 1 }));
                    });
                }
            } else {
                // 추첨제: 개인별 참여 데이터를 그대로 사용
                if (reset) {
                    setParticipations(participationsData);
                    const newOffset = participationsData.length;
                    setOffset(newOffset);
                    offsetRef.current = newOffset;
                } else {
                    // 이전 상태를 가져와서 계산
                    setParticipations(prev => {
                        const prevArray = Array.isArray(prev) ? prev : [];
                        const combined = [...prevArray, ...participationsData];
                        const unique = combined.filter(
                            (participation, index, self) =>
                                index === self.findIndex(p => p.id === participation.id)
                        );
                        return unique;
                    });
                    const newOffset = currentOffset + participationsData.length;
                    setOffset(newOffset);
                    offsetRef.current = newOffset;
                }
            }

            // 더 불러올 데이터가 있는지 확인
            const finalOffset = reset ? participationsData.length : currentOffset + participationsData.length;
            setHasMore(finalOffset < total && participationsData.length === ITEMS_PER_PAGE);
        } catch (err) {
            console.error('Failed to fetch participations:', err);
        } finally {
            if (reset) {
                setLoading(false);
            }
            setIsLoadingMore(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchParticipations는 의존성에서 제외
    }, [eventData?.id, eventDetail?.type, isLoadingMore]);

    // 결과 데이터 가져오기 (state=2) - 무한스크롤
    const fetchResults = useCallback(async (reset = false) => {
        if (!eventData?.id) return;
        if (isLoadingMoreResults && !reset) return;

        const currentOffset = reset ? 0 : resultsOffsetRef.current;

        if (reset) {
            setResultsLoading(true);
        } else {
            setIsLoadingMoreResults(true);
        }

        try {
            const resultsResponse = await eventAPI.getEventParticipations({
                eventId: eventData.id,
                expand: 'user',
                state: 2, // 결과 데이터만 조회
                offset: currentOffset,
                limit: ITEMS_PER_PAGE,
            });

            const resultsData = Array.isArray(resultsResponse?.data?.eventParticipations)
                ? resultsResponse.data.eventParticipations
                : Array.isArray(resultsResponse?.eventParticipations)
                    ? resultsResponse.eventParticipations
                    : Array.isArray(resultsResponse?.data)
                        ? resultsResponse.data
                        : [];

            const total = resultsResponse?.data?.total || resultsResponse?.total || 0;
            setResultsTotalCount(total);

            // 랭킹제(type=1)인 경우 기관별로 그룹화
            if (eventDetail?.type === 1) {
                if (reset) {
                    // 기관별로 그룹화
                    const orgMap = new Map();
                    resultsData.forEach((p) => {
                        const orgId = p.organizationId || 0;
                        const orgName = p.organizationId === 0
                            ? (p.user?.mainOrganizationName || t('no_affiliation'))
                            : (p.organization?.name || p.organizationName || t('no_affiliation'));

                        if (!orgMap.has(orgId)) {
                            orgMap.set(orgId, {
                                organizationId: orgId,
                                organizationName: orgName,
                                results: [],
                            });
                        }

                        orgMap.get(orgId).results.push({
                            userId: p.userId,
                            userName: p.user?.name || t('no_user'),
                            organizationName: orgName,
                            usedPoint: p.usedPoint || 0,
                            raw: p, // 원본 데이터 보관
                        });
                    });

                    // 각 기관별로 정렬 및 번호 매기기
                    const orgGroups = Array.from(orgMap.values()).map(org => {
                        const sortedResults = org.results
                            .sort((a, b) => (b.usedPoint || 0) - (a.usedPoint || 0))
                            .map((result, index) => ({
                                ...result,
                                no: index + 1,
                            }));

                        return {
                            ...org,
                            results: sortedResults,
                        };
                    });

                    setOrganizationResults(orgGroups);

                    // 각 기관별 통계 정보 가져오기 (API_PAGE_SIZE씩 배치)
                    const STATS_BATCH_SIZE = API_PAGE_SIZE;
                    const statisticsResults = [];
                    for (let i = 0; i < orgGroups.length; i += STATS_BATCH_SIZE) {
                        const batch = orgGroups.slice(i, i + STATS_BATCH_SIZE);
                        const batchPromises = batch.map(async (org) => {
                            try {
                                const statsResponse = await eventAPI.getEventParticipationsStatistics({
                                    eventId: eventData.id,
                                    organizationId: org.organizationId,
                                });

                                return {
                                    organizationId: org.organizationId,
                                    organizationName: org.organizationName,
                                    statistics: statsResponse?.data || statsResponse || {},
                                };
                            } catch (err) {
                                console.error(`Failed to fetch statistics for organization ${org.organizationId}:`, err);
                                return {
                                    organizationId: org.organizationId,
                                    organizationName: org.organizationName,
                                    statistics: {},
                                };
                            }
                        });
                        const batchResults = await Promise.all(batchPromises);
                        statisticsResults.push(...batchResults);
                    }
                    setOrganizationStatistics(statisticsResults);

                    setResults(resultsData);
                    setResultsOffset(resultsData.length);
                    resultsOffsetRef.current = resultsData.length;
                } else {
                    // 추가 데이터 로드 시 기존 구조 유지하면서 업데이트
                    setOrganizationResults(prev => {
                        const orgMap = new Map();

                        // 기존 데이터를 맵에 추가
                        prev.forEach(org => {
                            orgMap.set(org.organizationId, {
                                ...org,
                                results: [...org.results],
                            });
                        });

                        // 새 데이터를 기관별로 그룹화하여 추가
                        resultsData.forEach((p) => {
                            const orgId = p.organizationId || 0;
                            const orgName = p.organizationId === 0
                                ? (p.user?.mainOrganizationName || t('no_affiliation'))
                                : (p.organization?.name || p.organizationName || t('no_affiliation'));

                            if (!orgMap.has(orgId)) {
                                orgMap.set(orgId, {
                                    organizationId: orgId,
                                    organizationName: orgName,
                                    results: [],
                                });
                            }

                            // 중복 체크
                            const existingResult = orgMap.get(orgId).results.find(
                                r => r.userId === p.userId
                            );

                            if (!existingResult) {
                                orgMap.get(orgId).results.push({
                                    userId: p.userId,
                                    userName: p.user?.name || t('no_user'),
                                    organizationName: orgName,
                                    usedPoint: p.usedPoint || 0,
                                    raw: p,
                                });
                            }
                        });

                        // 각 기관별로 정렬 및 번호 재설정
                        const updatedGroups = Array.from(orgMap.values()).map(org => {
                            const sortedResults = org.results
                                .sort((a, b) => (b.usedPoint || 0) - (a.usedPoint || 0))
                                .map((result, index) => ({
                                    ...result,
                                    no: index + 1,
                                }));

                            return {
                                ...org,
                                results: sortedResults,
                            };
                        });

                        return updatedGroups;
                    });

                    setResults(prev => {
                        const combined = [...prev, ...resultsData];
                        // 중복 제거
                        const unique = combined.filter(
                            (item, index, self) =>
                                index === self.findIndex(p => p.id === item.id)
                        );
                        return unique;
                    });

                    const newOffset = currentOffset + resultsData.length;
                    setResultsOffset(newOffset);
                    resultsOffsetRef.current = newOffset;
                }
            } else {
                // 추첨제: 개인별 결과 데이터
                if (reset) {
                    setResults(resultsData);
                    setResultsOffset(resultsData.length);
                    resultsOffsetRef.current = resultsData.length;
                } else {
                    setResults(prev => {
                        const combined = [...prev, ...resultsData];
                        const unique = combined.filter(
                            (item, index, self) =>
                                index === self.findIndex(p => p.id === item.id)
                        );
                        return unique;
                    });
                    const newOffset = currentOffset + resultsData.length;
                    setResultsOffset(newOffset);
                    resultsOffsetRef.current = newOffset;
                }
            }

            // 더 불러올 데이터가 있는지 확인
            const finalOffset = reset ? resultsData.length : currentOffset + resultsData.length;
            setHasMoreResults(finalOffset < total && resultsData.length === ITEMS_PER_PAGE);
        } catch (err) {
            console.error('Failed to fetch results:', err);
        } finally {
            if (reset) {
                setResultsLoading(false);
            }
            setIsLoadingMoreResults(false);
        }
    }, [eventData?.id, eventDetail?.type, isLoadingMoreResults]);

    // 초기 참여 데이터 로드 (eventDetail이 로드된 후 실행)
    useEffect(() => {
        if (eventData?.id && eventDetail) {
            setOffset(0);
            offsetRef.current = 0;
            setHasMore(true);
            setParticipations([]);
            setOrganizationRankings([]);
            fetchParticipations(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchParticipations는 의존성에서 제외
    }, [eventData?.id, eventDetail?.type]);

    // 이벤트가 완료되었는지 확인 (status 값으로 판단)
    const isEventCompleted = () => {
        if (!eventDetail) return false;
        return eventDetail.status === 'finishing';
    };

    // 이벤트가 완료되었을 때 결과 데이터 로드 (의존성은 eventDetail.id, status만 사용 - isEventCompleted는 매 렌더 새 참조라 제외)
    useEffect(() => {
        if (eventDetail && eventDetail.status === 'finishing') {
            setResultsOffset(0);
            resultsOffsetRef.current = 0;
            setHasMoreResults(true);
            setResults([]);
            setOrganizationResults([]);
            setOrganizationStatistics([]);
            fetchResults(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchResults는 의존성에서 제외
    }, [eventDetail?.id, eventDetail?.status]);

    // 무한 스크롤 핸들러 (랭킹 테이블용)
    const handleScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(100px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoadingMore && !loading) {
            fetchParticipations(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchParticipations는 의존성에서 제외
    }, [hasMore, isLoadingMore, loading]);

    // 결과 테이블 무한 스크롤 핸들러
    const handleResultsScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(100px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMoreResults && !isLoadingMoreResults && !resultsLoading) {
            fetchResults(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchResults는 의존성에서 제외
    }, [hasMoreResults, isLoadingMoreResults, resultsLoading]);

    // 이벤트 상태 확인 (status 값으로 판단)
    const getEventStatus = () => {
        if (!eventDetail) return '';

        const status = eventDetail.status;

        if (status === 'waiting') {
            return t('application_has_not_started_yet');
        } else if (status === 'finishing') {
            return t('application_has_ended');
        } else if (status === 'ongoing') {
            return t('application_is_ongoing');
        }

        return t('application_is_ongoing');
    };

    // 편집 버튼 표시 여부 확인 (완료되지 않은 경우에만 표시)
    const canEdit = () => {
        if (!eventDetail) return false;
        // 완료(finishing)가 아니면 편집 가능
        return eventDetail.status !== 'finishing';
    };

    const handleEdit = () => {
        navigate('/events/edit', {
            state: {
                eventData: eventDetail || eventData,
            },
        });
    };

    if (!eventData || loading) {
        return (
            <ContentArea>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <Typography>{t('loading')}</Typography>
                </Box>
            </ContentArea>
        );
    }

    const eventType = EVENT_TYPES.find(type => type.value === (eventDetail?.type ?? eventData?.type ?? 0));

    return (
        <ContentArea>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 1.5, sm: 2, md: 2.5 },
                gap: { xs: 2, sm: 2.5, md: 3 }
            }}>
                {/* 상단: 이벤트 정보 및 랭킹 */}
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 2, sm: 2.5, md: 3 },
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%',
                    maxWidth: '100%',
                }}>
                    {/* 왼쪽: 이벤트 정보 및 이미지 */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        minWidth: 0,
                        width: { xs: '100%', md: 'auto' },
                    }}>
                        {/* 이벤트 타입 및 정보 */}
                        <Box>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    width: '100%',
                                    overflowX: 'auto',
                                }}
                            >
                                <Table size="small" sx={{ width: '100%' }}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ borderBottom: 'none', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                                                {t('event_type')}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', wordBreak: 'break-word' }}>
                                                {eventType ? t(eventType.label) : '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ borderBottom: 'none', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                                                {t('event_name')}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', wordBreak: 'break-word' }}>
                                                {eventDetail?.name || eventData?.name || '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ borderBottom: 'none', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                                                {t('event_winner_count')}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none' }}>
                                                {eventDetail?.targetCount || '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ borderBottom: 'none', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                                                {t('application_start_date')}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', fontSize: { xs: '12px', sm: '14px' } }}>
                                                {eventDetail?.startDate ? formatDateTimeDisplay(eventDetail.startDate, 'datetime') : '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ borderBottom: 'none', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                                                {t('application_end_date')}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', fontSize: { xs: '12px', sm: '14px' } }}>
                                                {eventDetail?.endDate ? formatDateTimeDisplay(eventDetail.endDate, 'datetime') : '-'}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        {/* 게시 이미지 */}
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                                    fontWeight: 600,
                                    mt: { xs: 1.5, md: 2 },
                                    mb: { xs: 1.5, md: 2 },
                                    color: theme.palette.figma?.white?.[100] || '#ffffff',
                                }}
                            >
                                {t('attach_post_image')}
                            </Typography>
                            {imageUrl ? (
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt={t('event_image')}
                                    sx={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        height: 'auto',
                                        borderRadius: '10px',
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        minHeight: '200px',
                                        backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                    }}
                                >
                                    {t('no_image')}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* 오른쪽: 총응모포인트 및 랭킹 */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        minWidth: 0,
                        width: { xs: '100%', md: 'auto' },
                    }}>
                        {/* 총응모포인트 */}
                        <Box>

                            <Box
                                sx={{
                                    borderRadius: '10px',
                                    p: { xs: 1.5, md: 2 },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                                        fontWeight: 600,
                                        color: theme.palette.figma?.white?.[100] || '#ffffff',
                                    }}
                                >
                                    {t('total_application_point')}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: { xs: '20px', sm: '22px', md: '24px' },
                                        fontWeight: 600,
                                        color: theme.palette.figma?.white?.[100] || '#ffffff',
                                    }}
                                >
                                    {(eventDetail?.totalParticipationUsedPoint || eventData?.totalParticipationUsedPoint || 0).toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>

                        {/* 랭킹 테이블 */}
                        <Box sx={{ width: '100%', overflow: 'hidden' }}>
                            <TableContainer
                                ref={tableRef}
                                component={Paper}
                                onScroll={handleScroll}
                                sx={{
                                    maxHeight: { xs: '300px', sm: '350px', md: '400px' },
                                    overflow: 'auto',
                                    width: '100%',
                                    '& .MuiTableCell-root': {
                                        fontSize: { xs: '11px', sm: '12px', md: '14px' },
                                        padding: { xs: '6px 8px', sm: '8px 10px', md: '16px' },
                                    },
                                }}
                            >
                                <Table stickyHeader size={isMobile ? 'small' : 'medium'} sx={{ width: '100%', minWidth: { xs: '500px', sm: 'auto' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '50px', sm: 'auto' } }}>{t('rank')}</TableCell>
                                            {eventDetail?.type === 1 ? (
                                                <>
                                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '80px', sm: 'auto' } }}>{t('org_top_rank')}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, minWidth: { xs: '120px', sm: 'auto' } }}>{t('main_organization_name')}</TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100px', sm: 'auto' } }}>{t('org_total_application')}</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '80px', sm: 'auto' } }}>{t('name')}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, minWidth: { xs: '120px', sm: 'auto' } }}>{t('main_organization_name')}</TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100px', sm: 'auto' } }}>{t('application_point')}</TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {eventDetail?.type === 1 ? (
                                            // 랭킹제: 기관별 데이터 표시
                                            organizationRankings.length > 0 ? (
                                                <>
                                                    {organizationRankings.map((org, index) => (
                                                        <TableRow
                                                            key={org.organizationId || index}
                                                            sx={getTableStyles(theme).tableRowStyle}
                                                        >
                                                            <TableCell align="center">
                                                                {org.rank}
                                                            </TableCell>
                                                            <TableCell>
                                                                {org.topUser || '-'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {org.organizationName || '-'}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {(org.totalPoint || 0).toLocaleString()}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {isLoadingMore && (
                                                        <TableRow>
                                                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2 }}>
                                                                <CircularProgress size={24} sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }} />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </>
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                                        {loading ? t('loading_data') : t('no_participation_data')}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        ) : (
                                            // 추첨제: 개인별 데이터 표시
                                            participations.length > 0 ? (
                                                <>
                                                    {participations.map((participation, index) => (
                                                        <TableRow
                                                            key={participation.id || index}
                                                            sx={getTableStyles(theme).tableRowStyle}
                                                        >
                                                            <TableCell align="center">
                                                                {index + 1}
                                                            </TableCell>
                                                            <TableCell>
                                                                {participation.user?.name || '-'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {participation.organizationId === 0
                                                                    ? (participation.user?.mainOrganizationName || t('no_affiliation'))
                                                                    : (participation.organization?.name || participation.organizationName || t('no_affiliation'))}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {(participation.usedPoint || 0).toLocaleString()}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {isLoadingMore && (
                                                        <TableRow>
                                                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2 }}>
                                                                <CircularProgress size={24} sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }} />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </>
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                                        {loading ? t('loading_data') : t('no_participation_data')}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {/* 상태 표시 */}
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ color: theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)' }}>
                                    {getEventStatus()}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* 결과 섹션 - 이벤트가 완료된 경우에만 표시 */}
                {isEventCompleted() && (
                    <Box sx={{
                        width: '100%',
                        mt: 2,
                    }}>
                        {/* 결과 헤더 */}
                        <Typography
                            variant="h5"
                            sx={{
                                fontSize: { xs: '18px', sm: '20px', md: '24px' },
                                fontWeight: 700,
                                mb: 2,
                                color: theme.palette.figma?.white?.[100] || '#ffffff',
                            }}
                        >
                            {eventDetail?.type === 1 ? t('result') : t('lottery_result')}
                        </Typography>

                        {eventDetail?.type === 0 && (
                            <Typography
                                sx={{
                                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                    mb: 2,
                                }}
                            >
                                {t('result_notified_after_deadline')}
                            </Typography>
                        )}

                        {resultsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }} />
                            </Box>
                        ) : eventDetail?.type === 1 ? (
                            // 랭킹제 결과 - 기관별로 표시
                            organizationResults.length > 0 ? (
                                <>
                                    {/* 기관별로 반복 표시 */}
                                    {organizationResults.map((orgGroup, orgIndex) => {
                                        // 해당 기관의 통계 정보 찾기
                                        const orgStats = organizationStatistics.find(
                                            s => s.organizationId === orgGroup.organizationId
                                        );

                                        return (
                                            <Box key={orgGroup.organizationId || orgIndex} sx={{ mb: 3 }}>
                                                {/* 기관별 요약 바 */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        width: '100%',
                                                        borderRadius: '10px',
                                                        overflow: 'hidden',
                                                        border: `1px solid ${theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)'}`,
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Box sx={{
                                                        backgroundColor: theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)',
                                                        padding: '12px 16px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        minWidth: '150px',
                                                    }}>
                                                        <Typography sx={{
                                                            fontSize: { xs: '14px', sm: '15px', md: '16px' },
                                                            color: theme.palette.figma?.white?.[100] || '#ffffff',
                                                            fontWeight: 500,
                                                        }}>
                                                            {orgGroup.organizationName || '-'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{
                                                        backgroundColor: theme.palette.figma?.block || 'rgba(234, 232, 243, 0.2)',
                                                        padding: '12px 16px',
                                                        flex: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                    }}>
                                                        <Typography sx={{
                                                            fontSize: { xs: '16px', sm: '18px', md: '20px' },
                                                            color: theme.palette.figma?.white?.[100] || '#ffffff',
                                                            fontWeight: 700,
                                                        }}>
                                                            {orgStats?.statistics?.eventParticipationsStatistics?.[0]?.organizationTotalUsedPoint?.toLocaleString() ||
                                                                orgGroup.results.reduce((sum, r) => sum + (r.usedPoint || 0), 0).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* 기관 내 참여자 테이블 */}
                                                <TableContainer
                                                    component={Paper}
                                                    ref={orgIndex === 0 ? resultsTableRef : null}
                                                    onScroll={orgIndex === 0 ? handleResultsScroll : undefined}
                                                    sx={{
                                                        maxHeight: { xs: '250px', sm: '300px', md: '350px' },
                                                        overflow: 'auto',
                                                        borderRadius: '10px',
                                                        '& .MuiTableCell-root': {
                                                            fontSize: { xs: '11px', sm: '12px', md: '14px' },
                                                            padding: { xs: '6px 8px', sm: '8px 10px', md: '16px' },
                                                        },
                                                    }}
                                                >
                                                    <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '50px', sm: 'auto' } }}>{t('number')}</TableCell>
                                                                <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '80px', sm: 'auto' } }}>{t('name')}</TableCell>
                                                                <TableCell align="center" sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, minWidth: { xs: '120px', sm: 'auto' } }}>{t('main_organization_name')}</TableCell>
                                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100px', sm: 'auto' } }}>{t('application_point')}</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {orgGroup.results.map((result, index) => (
                                                                <TableRow
                                                                    key={result.userId || index}
                                                                    sx={getTableStyles(theme).tableRowStyle}
                                                                >
                                                                    <TableCell align="center">{result.no}</TableCell>
                                                                    <TableCell align="center">{result.userName}</TableCell>
                                                                    <TableCell align="center">{result.organizationName}</TableCell>
                                                                    <TableCell align="right">{result.usedPoint.toLocaleString()}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                            {isLoadingMoreResults && orgIndex === 0 && (
                                                                <TableRow>
                                                                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2 }}>
                                                                        <CircularProgress size={24} sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }} />
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        );
                                    })}
                                </>
                            ) : (
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                    <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                                        {t('no_result_data')}
                                    </Typography>
                                </Box>
                            )
                        ) : (
                            // 추첨제 결과 - 개인별 표시
                            <TableContainer
                                ref={resultsTableRef}
                                component={Paper}
                                onScroll={handleResultsScroll}
                                sx={{
                                    maxHeight: { xs: '300px', sm: '350px', md: '400px' },
                                    overflow: 'auto',
                                    '& .MuiTableCell-root': {
                                        fontSize: { xs: '11px', sm: '12px', md: '14px' },
                                        padding: { xs: '6px 8px', sm: '8px 10px', md: '16px' },
                                    },
                                }}
                            >
                                <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '50px', sm: 'auto' } }}>{t('number')}</TableCell>
                                            <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '80px', sm: 'auto' } }}>{t('name')}</TableCell>
                                            <TableCell align="center" sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, minWidth: { xs: '120px', sm: 'auto' } }}>{t('main_organization_name')}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100px', sm: 'auto' } }}>{t('application_point')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.length > 0 ? (
                                            <>
                                                {results.map((result, index) => (
                                                    <TableRow
                                                        key={result.id || index}
                                                        sx={getTableStyles(theme).tableRowStyle}
                                                    >
                                                        <TableCell align="center">{index + 1}</TableCell>
                                                        <TableCell align="center">{result.user?.name || '-'}</TableCell>
                                                        <TableCell align="center">
                                                            {result.organizationId === 0
                                                                ? (result.user?.mainOrganizationName || t('no_affiliation'))
                                                                : (result.organization?.name || result.organizationName || t('no_affiliation'))}
                                                        </TableCell>
                                                        <TableCell align="right">{(result.usedPoint || 0).toLocaleString()}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {isLoadingMoreResults && (
                                                    <TableRow>
                                                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2 }}>
                                                            <CircularProgress size={24} sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }} />
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                                    {t('no_result_data')}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}

                {/* 하단: 버튼 */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'flex-end' },
                    alignItems: 'center',
                    mt: { xs: 1.5, md: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1.5, sm: 2 },
                }}>
                    <Box sx={{
                        display: 'flex',
                        gap: { xs: 1, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                        flexDirection: { xs: 'column', sm: 'row' },
                    }}>
                        {canEdit() && (
                            <Button
                                onClick={handleEdit}
                                sx={{
                                    backgroundColor: theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)',
                                    color: theme.palette.figma?.white?.[100] || '#ffffff',
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                        backgroundColor: theme.palette.figma?.white?.[20] || 'rgba(255, 255, 255, 0.2)',
                                    },
                                }}
                            >
                                {t('edit_button')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </ContentArea>
    );
};

export default EventAdminDetail;
